import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  Input,
  OnInit,
  output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MenuItemType } from '@/app/types/layout';
import { CommonModule } from '@angular/common';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { LayoutService } from '@core/services/layout-store.service';
import { scrollToElement } from '@/app/utils/layout-utils';
import { UiTranslationService } from '@core/services/ui-translation.service';

@Component({
  selector: 'app-menu',
  imports: [CommonModule, NgbCollapseModule, RouterLink],
  templateUrl: './app-menu.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppMenuComponent implements OnInit {
  router = inject(Router);
  layout = inject(LayoutService);
  private readonly navigationTranslations =
    inject(UiTranslationService).namespace('navigation');
  @Input({ required: true }) menuItems: MenuItemType[] = [];
  readonly menuAction = output<MenuItemType['action']>();

  @ViewChild('MenuItemWithChildren', { static: true })
  menuItemWithChildren!: TemplateRef<{ item: MenuItemType }>;

  @ViewChild('MenuItem', { static: true })
  menuItem!: TemplateRef<{ item: MenuItemType }>;

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.expandActivePaths(this.menuItems);
        setTimeout(() => this.scrollToActiveLink(), 50);
      });

    this.expandActivePaths(this.menuItems);
    setTimeout(() => this.scrollToActiveLink(), 100);
  }

  hasSubMenu(item: MenuItemType): boolean {
    return !!item.children;
  }

  expandActivePaths(items: MenuItemType[]) {
    for (const item of items) {
      if (this.hasSubMenu(item)) {
        item.isCollapsed = !this.isChildActive(item);
        this.expandActivePaths(item.children || []);
      }
    }
  }

  isChildActive(item: MenuItemType): boolean {
    if (item.url && this.router.url === item.url) return true;
    if (!item.children) return false;
    return item.children.some((child: MenuItemType) =>
      this.isChildActive(child),
    );
  }

  isActive(item: MenuItemType): boolean {
    return this.router.url === item.url;
  }

  runAction(item: MenuItemType): void {
    if (item.isDisabled || !item.action) {
      return;
    }

    this.menuAction.emit(item.action);
  }

  resolveLabel(item: MenuItemType): string {
    if (!item.labelKey) {
      return item.label;
    }

    const translated = this.navigationTranslations.tr(item.labelKey);
    return translated === item.labelKey ? item.label : translated;
  }

  scrollToActiveLink(): void {
    const activeItem = document.querySelector(
      '[data-active-link="true"]',
    ) as HTMLElement;
    const scrollContainer = document.querySelector(
      '#sidenav .simplebar-content-wrapper',
    ) as HTMLElement;

    if (activeItem && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();

      const offset =
        itemRect.top - containerRect.top - window.innerHeight * 0.4;

      scrollToElement(scrollContainer, scrollContainer.scrollTop + offset, 500);
    }
  }

  toggleCollaspe(item: MenuItemType, siblings: MenuItemType[] = []) {
    item.isCollapsed = !item.isCollapsed;

    if (!item.isCollapsed && Array.isArray(siblings)) {
      for (const sib of siblings) {
        if (sib !== item) sib.isCollapsed = true;
      }
    }
  }

  expandFilteredPaths(items: MenuItemType[]) {
    for (const item of items) {
      if (this.hasSubMenu(item)) {
        item.isCollapsed = false;
        this.expandFilteredPaths(item.children || []);
      }
    }
  }
}
